import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";
import { Col, Row } from "reactstrap";
import ButtonComponent from "../../components/ButtonComponent";
import InputComponent, { InputComponentProps } from "../../components/InputComponent";
import Layout from "../../components/Layout/Layout";
import TableComponent, { OperationsCell } from "../../components/TableComponent";
import { Link } from "react-router-dom";
import { FcViewDetails } from "react-icons/fc";
import { useAvailableModulesContext } from "../../context/AvailableModulesContext";
import Routes from "../../helpers/Routes";
import Toast from "../../helpers/Toast";
import { Utilities } from "../../helpers/Utilities";
import { Parameter } from "../../models/Parameter";
import { ClientQuotationService } from "../../services/ClientQuotationService";
import { ClientQuotationDTO } from "../../models/Quotation/ClientQuotationDTO";
import { AssistanceDTO } from "../../models/Assistance/AssistanceDTO";
import { ClientService } from "../../services/ClientService";
import { AssistanceService } from "../../services/AssistanceService";
import { SelectItemsProps } from "../client/ClientEdit";
import { GetDocNumberDTO } from "../../models/Assistance/GetDocNumberDTO";
import { SearchByClientName } from "../../models/Client/SearchByClientName";
import SearchSelectComponent from "../../components/SearchSelectComponent";
import MultiSelectComponent from "../../components/MultiSelectComponent";
import "../../styles/index.css";

export default function ClientQuotationIndex() {
    const { availableModules } = useAvailableModulesContext();
    const intl = useIntl();

    const navigate = useNavigate();
    const clientService = new ClientService();
    const clientQuotationService = new ClientQuotationService();
    const assistanceService = new AssistanceService();
    const pageSizes: number[] = [5, 15, 30];
    const [data, setData] = useState<ClientQuotationDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [searchParams] = useSearchParams();
    const [selectedClient, setSelectedClient] = useState<SelectItemsProps | null>(null);
    const [selectedAssistance, setSelectedAssistance] = useState<SelectItemsProps | null>(null);
    const [listStatus, setListStatus] = useState<SelectItemsProps[]>([]);

    const [page] = useState<number>(Utilities.LoadPageFromURLQuery("page"));
    const [pageSize] = useState<number>(
        Utilities.LoadPageSizeFromURLQuery(pageSizes)
    );


    const [id, setId] = useState<string>(
        Utilities.LoadParameterFromURLQuery("id", "string", "")
    );
    const [client, setClient] = useState<string>(
        Utilities.LoadParameterFromURLQuery("client", "string", "")
    );
    const [status, setStatus] = useState<string>(
        Utilities.LoadParameterFromURLQuery("status", "string", "")
    );
    const [assistance, setAssistance] = useState<string>(
        Utilities.LoadParameterFromURLQuery("assistance", "string", "")
    );


    const [currentPage, setCurrentPage] = useState<number>();
    const [currentPageSize, setCurrentPageSize] = useState<number>();
    const [currentSorting, setCurrentSorting] = useState<
        SortingRule<object>[] | null
    >(null);
    const [goToPage, setGoToPage] = useState<number>();
    const [initialSortBy] = useState<SortingRule<object>[]>(
        Utilities.LoadSortParametersFromURLQuery("sortField", "sortOrder")
    );
    const [reloadSearch, setReloadSearch] = useState<boolean>(false);


    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);

    const columns = React.useMemo(
        () => [
            {
                Header: "",
                id: "operations",
                width: 10,
                Cell: ({ row }: any) => {
                    const quotation: ClientQuotationDTO = row.original;
                    const isActiveErpClientQuotation = availableModules?.includes("IsActiveErpClientQuotation");
                    return (
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                            {isActiveErpClientQuotation
                                ? (
                                    <Link
                                        to="/clientQuotation/details"
                                        state={{
                                            docType: quotation.docType,
                                            serie: quotation.serie,
                                            number: quotation.number
                                        }}
                                    >
                                        <FcViewDetails className="icons" />
                                    </Link>
                                )
                                : OperationsCell(quotation.id.toString(), "clientQuotation")}
                        </div>
                    );
                },
            },
            {
                Header: <FormattedMessage id="Screens.ClientQuotation.Table.QuotationNumber" />,
                accessor: "id",
                width: 15,
                Cell: (props: any) => {
                    const { docType, serie, number, id } = props.cell.row.original;
                    if (!docType || !serie || number == null) {
                        return id;
                    }
                    return Utilities.FormattedAssistanceDocument(docType, serie, number);
                },
            },
            {
                Header: <FormattedMessage id="Screens.ClientQuotation.Table.Client" />,
                accessor: "client.name",
                width: 40,
            },
            {
                Header: <FormattedMessage id="Screens.ClientQuotation.Table.Status" />,
                accessor: "status.frontendName",
                width: 20,
            },
            {
                Header: <FormattedMessage id="Screens.ClientQuotation.Table.Assistance" />,
                accessor: "assistance",
                Cell: (props: { value: AssistanceDTO | null }) => {
                    if (!props.value) return null;
                    return (
                        <span>
                            {props.value.erpDocType} {props.value.erpSerie} {props.value.erpNumDoc}
                        </span>
                    );
                },
                width: 10,
            },
            {
                Header: <FormattedMessage id="Screens.ClientQuotation.Table.TotalAmount" />,
                accessor: "sellingPrice",
                width: 10,
            },
        ],
        []
    );

    const fetchData = async (
        page: number,
        pageSize: number,
        sortBy: SortingRule<object>[] | null
    ) => {


        var searchUrl: string = `?page=${page}&pageSize=${pageSize}` +
            `${Utilities.AddParamToQueryIfValue("id", "string", id)}` +
            `${Utilities.AddParamToQueryIfValue("client", "string", client)}` +
            `${Utilities.AddParamToQueryIfValue("status", "string", status)}` +
            `${Utilities.AddParamToQueryIfValue("assistance", "string", assistance)}`;

        if (sortBy != null && sortBy.length > 0) {
            searchUrl += `${Utilities.AddParamToQueryIfValue(
                "sortField",
                "string",
                sortBy[0].id
            )}${Utilities.AddParamToQueryIfValue(
                "sortOrder",
                "string",
                sortBy[0].desc === true ? "DESC" : "ASC"
            )}`;
        }


        if (searchParams.toString() === "") {
            navigate(`/clientQuotations${searchUrl}`, { replace: true });
        } else {

            navigate(`/clientQuotations${searchUrl}`);
        }

        setLoading(true);


        var response = await clientQuotationService.GetAll(
            mountSearchParameters(),
            Utilities.MountSortParameters(sortBy),
            page,
            pageSize
        );

        if (response.success !== true) {
            setData([]);
            setPageCount(0);
            setLoading(false);
            setCurrentPageSize(5);
            setCurrentPage(1);
            setCurrentSorting(null);
            Toast.Show("error", response.message);
            return;
        }

        setData(response.items);
        setPageCount(response.totalPages);
        setLoading(false);
        setCurrentPageSize(pageSize);
        setCurrentPage(page);
        setCurrentSorting(sortBy);
        setGoToPage(0);


        if (response.totalPages > 0 && page > response.totalPages) {
            setGoToPage(1);
        }
    };


    const mountSearchParameters = (): Parameter[] => {
        var parameters: Parameter[] = [];

        parameters.push(new Parameter("id", id));
        parameters.push(new Parameter("client", client));
        parameters.push(new Parameter("status", status));
        parameters.push(new Parameter("assistance", assistance));

        return parameters;
    };

    const onEnter = () => {
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onButtonClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
    };
    const onRemoveFiltersClick = async () => {
        setCurrentPage(1);
        setGoToPage(1);
        setId("");
        setClient("");
        setSelectedClient(null);
        setStatus("");
        setAssistance("");
        setSelectedAssistance(null);
        setReloadSearch(true);
    };

    useEffect(() => {
        if (reloadSearch === true) {
            fetchData(currentPage ?? 1, currentPageSize ?? 5, currentSorting);
            setReloadSearch(false);
        }
        return () => { };
    }, [reloadSearch]);



    const onNewTaskClick = () => {
        navigate(Routes.clientQuotations.create);
    };

    const searchClient = async (
        name: string
    ): Promise<SelectItemsProps[]> => {
        const searchParams: SearchByClientName = { name };

        const response = await clientService.SearchByName(searchParams);

        if (response.success === false || response.obj === null) {
            Toast.Show("error", response.message);
        }

        const list: SelectItemsProps[] = [];
        response.obj?.forEach((element) => {
            const item: SelectItemsProps = {
                value: element.erpId,
                label: element.name,
            };
            list.push(item);
        });

        return list;
    };

    const getStatus = async () => {
        const clientQuotationService = new ClientQuotationService();
        const response = await clientQuotationService.GetAllStatus();
        const searchParameters: Parameter[] = [];
        if (response.success === false) {
            Toast.Show("error", response.message);
            return;
        }

        const items: SelectItemsProps[] = response.obj.map((element) => ({
            value: element.frontendName,
            label: element.frontendName,
        }));

        setListStatus(items);
    }
    useEffect(() => {
        getStatus();
    }, []);
    const searchAssistance = async (
        docNumber: number
    ): Promise<SelectItemsProps[]> => {
        const searchParams: GetDocNumberDTO = { docNumber };
        const parameters: Parameter[] = [
            new Parameter('numDoc', docNumber),
        ];
        const response = await assistanceService.GetAll(parameters, [], 1, 10);

        if (response.success === false || !response.items) {
            Toast.Show("error", response.message);
        }

        const list: SelectItemsProps[] = [];
        response.items?.forEach((element) => {
            const item: SelectItemsProps = {
                value: element.assistanceId?.toString() ?? "",
                label: element.docType + " " + element.serie.toString() + " " + element.number.toString(),
            };
            list.push(item);
        });
        return list;
    };

    return (
        <>
            <Layout>
                <div className="pageContainer">
                    <div className="pageHeader">
                        <span className="pageTitle">
                            <FormattedMessage id="Screens.ClientQuotation.Title" />
                        </span>

                        <Row>
                            <Col
                                xl={2}
                                lg={3}
                                md={3}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", marginBottom: "1em" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Screens.ClientQuotation.Button.CreateClientQuotation" })}
                                    onClick={onNewTaskClick}
                                    disabled={false} />
                            </Col>
                        </Row>
                    </div>

                    <div className="pageBody">
                        <Row>
                            <InputComponent
                                xl={1}
                                lg={2}
                                md={2}
                                sm={12}
                                xs={12}
                                type="text"
                                onEnter={onEnter}
                                value={id}
                                label={intl.formatMessage({ id: "Screens.ClientQuotation.SearchField.QuotationNumber" })}
                                readOnly={false}
                                onChange={(t) => setId(t)}
                                style={{ width: "100%" }}
                            />

                            <SearchSelectComponent
                                xl={2}
                                lg={4}
                                md={4}
                                sm={12}
                                xs={12}
                                label={intl.formatMessage({ id: "Screens.ClientQuotation.SearchField.Client" })}
                                loadOptions={searchClient}
                                onChange={(e) => {
                                    setSelectedClient(e);
                                    setClient(e.value);
                                }}
                                value={selectedClient}
                                style={{ width: "100%" }}
                            />

                            <MultiSelectComponent
                                xl={2}
                                lg={4}
                                md={4}
                                sm={12}
                                xs={12}
                                label={intl.formatMessage({ id: "Screens.ClientQuotation.SearchField.Status" })}
                                items={listStatus}
                                setSelectedString={setStatus}
                                selectString={status}
                                style={{ width: "100%" }}
                            />

                            <SearchSelectComponent
                                xl={2}
                                lg={4}
                                md={4}
                                sm={12}
                                xs={12}
                                label={intl.formatMessage({ id: "Screens.ClientQuotation.SearchField.Assistance" })}
                                loadOptions={(value: string) => searchAssistance(Number(value))}
                                onChange={(e) => {
                                    setSelectedAssistance(e);
                                    setAssistance(e.value);
                                }}
                                value={selectedAssistance}
                                style={{ width: "100%" }}
                            />
                        </Row>

                        <Row>
                            <Col
                                xl={6}
                                lg={6}
                                md={6}
                                sm={12}
                                xs={12}
                                style={{ marginTop: "1em", display: "inline-flex", gap: "1rem" }}
                            >
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Search" })}
                                    onClick={onButtonClick}
                                    style={{ minWidth: "100px" }}
                                />
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.RemoveFilters" })}
                                    onClick={onRemoveFiltersClick}
                                    style={{ minWidth: "113px" }}
                                />
                            </Col>
                        </Row>

                        <div className="pageComponent" style={{ width: "100%" }}>
                            <TableComponent
                                goToPage={goToPage}
                                loading={loading}
                                pageCount={pageCount}
                                columns={columns}
                                manualPagination={true}
                                fetchData={fetchData}
                                pageSizes={pageSizes}
                                initialPage={page}
                                initialPageSize={pageSize}
                                initialSortBy={initialSortBy}
                                data={data}
                                dontOrderFirstCollumn={true}
                            />
                        </div>
                    </div>
                </div>
            </Layout>
        </>
    );
}
